import { useEffect, useRef, useState } from "react";
import { customerOwnerOptions, hasActiveCustomerOwner } from "../lib/customerAccess.js";
import { currency } from "../lib/format.js";
import { CustomerLink, EmployeeLink } from "../components/recordLinks.jsx";
import { AsyncActionButton, Panel, ResourceTable, SelectField, SimpleForm, TextField, Topbar } from "../components/ui.jsx";

export function ProductsOrdersView({
  resources,
  customers,
  users,
  user,
  onCreate,
  onUpdate,
  openCustomer,
  openEmployee,
  openMessage,
  workflowDraft,
  onWorkflowDraftHandled,
}) {
  const canManageCatalog = user.role === "manager" || user.role === "admin";
  const hasCustomers = customers.length > 0;
  const hasProducts = resources.products.length > 0;
  const customerOptions = hasCustomers ? customerOwnerOptions(customers, users) : [{ value: "", label: "No customers available" }];
  const recommendationProductOptions = hasProducts
    ? resources.products.map((product) => ({ value: product.id, label: `${product.name} (${product.sku})` }))
    : [{ value: "", label: "No products available" }];
  const pricedProductOptions = hasProducts
    ? resources.products.map((product) => ({ value: product.id, label: `${product.name} - ${currency(product.sellingPrice)}` }))
    : [{ value: "", label: "No products available" }];
  const canUseCustomerProductForms = hasCustomers && hasProducts;
  const [productForm, setProductForm] = useState(emptyProductForm);
  const [editingProductId, setEditingProductId] = useState("");
  const [recommendationForm, setRecommendationForm] = useState({ customerId: customers[0]?.id ?? "", productId: resources.products[0]?.id ?? "", need: "", budget: "", timeline: "", competitor: "", notes: "" });
  const [orderForm, setOrderForm] = useState({ customerId: customers[0]?.id ?? "", productId: resources.products[0]?.id ?? "", quantity: 1, sellingPrice: resources.products[0]?.sellingPrice ?? 0, discount: 0, status: "Draft" });
  const [pricingForm, setPricingForm] = useState({ customerId: customers[0]?.id ?? "", productId: resources.products[0]?.id ?? "", sellingPrice: resources.products[0]?.sellingPrice ?? 0, discount: 0 });
  const productEditorRef = useRef(null);
  const recommendationFormRef = useRef(null);
  const pricingFormRef = useRef(null);
  const orderFormRef = useRef(null);
  const customerIds = customers.map((customer) => customer.id).join("|");
  const productIds = resources.products.map((product) => product.id).join("|");
  const canSaveRecommendation = canUseCustomerProductForms && hasActiveCustomerOwner(recommendationForm.customerId, customers, users);
  const canSavePricing = canUseCustomerProductForms && hasActiveCustomerOwner(pricingForm.customerId, customers, users);
  const orderHasCreditBlock = resources.outstanding.some(
    (record) =>
      record.customerId === orderForm.customerId &&
      (record.creditBlocked || Number(record.outstandingAmount || 0) > Number(record.creditLimit || 250000)),
  );
  const orderHasActiveOwner = hasActiveCustomerOwner(orderForm.customerId, customers, users);
  const canSaveOrder = canUseCustomerProductForms && orderHasActiveOwner && !orderHasCreditBlock;

  useEffect(() => {
    const fallbackCustomerId = customers[0]?.id || "";
    const fallbackProduct = resources.products[0];
    const fallbackProductId = fallbackProduct?.id || "";
    const resolveCustomerId = (currentId) => (customers.some((customer) => customer.id === currentId) ? currentId : fallbackCustomerId);
    const resolveProduct = (currentId) => {
      const product = resources.products.find((item) => item.id === currentId);
      return product || fallbackProduct || null;
    };

    setRecommendationForm((current) => ({
      ...current,
      customerId: resolveCustomerId(current.customerId),
      productId: resolveProduct(current.productId)?.id || fallbackProductId,
    }));
    setOrderForm((current) => {
      const product = resolveProduct(current.productId);
      const productChanged = product?.id && product.id !== current.productId;
      return {
        ...current,
        customerId: resolveCustomerId(current.customerId),
        productId: product?.id || fallbackProductId,
        sellingPrice: productChanged ? product.sellingPrice : current.sellingPrice,
      };
    });
    setPricingForm((current) => {
      const product = resolveProduct(current.productId);
      const productChanged = product?.id && product.id !== current.productId;
      return {
        ...current,
        customerId: resolveCustomerId(current.customerId),
        productId: product?.id || fallbackProductId,
        sellingPrice: productChanged ? product.sellingPrice : current.sellingPrice,
      };
    });
  }, [customerIds, productIds]);

  useEffect(() => {
    if (!workflowDraft || workflowDraft.type !== "order") return;
    const customerExists = customers.some((customer) => customer.id === workflowDraft.customerId);
    const product = workflowDraft.productId
      ? productByReference(workflowDraft.productId, workflowDraft.productName)
      : resources.products[0];
    if (!customerExists) {
      onWorkflowDraftHandled?.(workflowDraft.id);
      return;
    }
    if (!product) return;
    setOrderForm({
      customerId: workflowDraft.customerId,
      productId: product.id,
      quantity: Number(workflowDraft.quantity || 1),
      sellingPrice: Number(workflowDraft.sellingPrice ?? product.sellingPrice ?? 0),
      discount: Number(workflowDraft.discount || 0),
      status: workflowDraft.status || "Draft",
    });
    focusLinkedFormAfterRender(orderFormRef);
    onWorkflowDraftHandled?.(workflowDraft.id);
  }, [workflowDraft?.id, customerIds, productIds]);

  return (
    <>
      <Topbar title="Products & Orders" subtitle="Catalog search, probing, recommendations, customer pricing, margin approvals, orders, and recommendation-to-order tracking." />
      <section className="content-grid">
        {canManageCatalog ? (
          <div className="product-editor-anchor" ref={productEditorRef}>
            <Panel
              title={editingProductId ? "Edit Product / SKU" : "Add Product / SKU"}
              subtitle={editingProductId ? "The selected catalog item is loaded here. Update the fields and save to apply changes." : undefined}
            >
              <SimpleForm onSubmit={saveProduct} submitLabel={editingProductId ? "Update Product" : "Save Product"} submittingLabel={editingProductId ? "Updating..." : "Saving..."}>
                <TextField label="Product name" value={productForm.name} required onChange={(name) => setProductForm({ ...productForm, name })} />
                <TextField label="SKU code" value={productForm.sku} required onChange={(sku) => setProductForm({ ...productForm, sku })} />
                <TextField label="Category" value={productForm.category} onChange={(category) => setProductForm({ ...productForm, category })} />
                <TextField label="Cost price" type="number" value={productForm.costPrice} onChange={(costPrice) => setProductForm({ ...productForm, costPrice })} />
                <TextField label="Selling price" type="number" value={productForm.sellingPrice} onChange={(sellingPrice) => setProductForm({ ...productForm, sellingPrice })} />
                <TextField label="Availability" value={productForm.availability} onChange={(availability) => setProductForm({ ...productForm, availability })} />
                <TextField label="Customization options" value={productForm.customizationOptions} onChange={(customizationOptions) => setProductForm({ ...productForm, customizationOptions })} />
                {editingProductId ? (
                  <div className="row-actions full">
                    <button className="ghost-btn" type="button" onClick={resetProductForm}>
                      Cancel Edit
                    </button>
                  </div>
                ) : null}
              </SimpleForm>
            </Panel>
          </div>
        ) : null}
        <div ref={recommendationFormRef}>
          <Panel title="Guided Recommendation">
            {!canUseCustomerProductForms ? <div className="notice">Add at least one customer and one product before saving recommendations, pricing, or orders.</div> : null}
            {canUseCustomerProductForms && !canSaveRecommendation ? <div className="notice">Selected customer is assigned to an inactive sales rep. Reassign the prospect before saving recommendations.</div> : null}
            <SimpleForm disabled={!canSaveRecommendation} onSubmit={() => onCreate("/recommendations", recommendationForm)}>
              <SelectField
                label="Customer"
                value={recommendationForm.customerId}
                options={customerOptions}
                disabled={!hasCustomers}
                actionLabel="Add prospect"
                onAction={openCustomer ? () => openCustomer(null) : undefined}
                secondaryActionLabel="Open selected prospect"
                secondaryActionDisabled={!recommendationForm.customerId}
                onSecondaryAction={openCustomer ? () => openCustomer(recommendationForm.customerId) : undefined}
                onChange={(customerId) => setRecommendationForm({ ...recommendationForm, customerId })}
              />
              <SelectField
                label="Product"
                value={recommendationForm.productId}
                options={recommendationProductOptions}
                disabled={!hasProducts}
                actionLabel="Add product"
                onAction={canManageCatalog ? focusProductEditor : undefined}
                secondaryActionLabel="Edit selected product"
                secondaryActionDisabled={!recommendationForm.productId}
                onSecondaryAction={canManageCatalog ? () => editSelectedProduct(recommendationForm.productId) : undefined}
                onChange={(productId) => setRecommendationForm({ ...recommendationForm, productId })}
              />
              <TextField label="Need / use case" value={recommendationForm.need} onChange={(need) => setRecommendationForm({ ...recommendationForm, need })} />
              <TextField label="Budget" value={recommendationForm.budget} onChange={(budget) => setRecommendationForm({ ...recommendationForm, budget })} />
              <TextField label="Timeline" value={recommendationForm.timeline} onChange={(timeline) => setRecommendationForm({ ...recommendationForm, timeline })} />
              <TextField label="Competitor" value={recommendationForm.competitor} onChange={(competitor) => setRecommendationForm({ ...recommendationForm, competitor })} />
            </SimpleForm>
          </Panel>
        </div>
      </section>
      <section className="content-grid spaced">
        <div ref={pricingFormRef}>
          <Panel title="Customer Pricing">
            {!canUseCustomerProductForms ? <div className="notice">Add at least one customer and one product before saving customer pricing.</div> : null}
            {canUseCustomerProductForms && !canSavePricing ? <div className="notice">Selected customer is assigned to an inactive sales rep. Reassign the prospect before saving customer pricing.</div> : null}
            <SimpleForm disabled={!canSavePricing} onSubmit={() => onCreate("/pricing", pricingForm)}>
              <SelectField
                label="Customer"
                value={pricingForm.customerId}
                options={customerOptions}
                disabled={!hasCustomers}
                actionLabel="Add prospect"
                onAction={openCustomer ? () => openCustomer(null) : undefined}
                secondaryActionLabel="Open selected prospect"
                secondaryActionDisabled={!pricingForm.customerId}
                onSecondaryAction={openCustomer ? () => openCustomer(pricingForm.customerId) : undefined}
                onChange={(customerId) => setPricingForm({ ...pricingForm, customerId })}
              />
              <SelectField
                label="Product"
                value={pricingForm.productId}
                options={pricedProductOptions}
                disabled={!hasProducts}
                actionLabel="Add product"
                onAction={canManageCatalog ? focusProductEditor : undefined}
                secondaryActionLabel="Edit selected product"
                secondaryActionDisabled={!pricingForm.productId}
                onSecondaryAction={canManageCatalog ? () => editSelectedProduct(pricingForm.productId) : undefined}
                onChange={(productId) => {
                  const product = resources.products.find((item) => item.id === productId);
                  setPricingForm({ ...pricingForm, productId, sellingPrice: product?.sellingPrice ?? pricingForm.sellingPrice });
                }}
              />
              <TextField label="Customer selling price" type="number" value={pricingForm.sellingPrice} onChange={(sellingPrice) => setPricingForm({ ...pricingForm, sellingPrice })} />
              <TextField label="Discount" type="number" value={pricingForm.discount} onChange={(discount) => setPricingForm({ ...pricingForm, discount })} />
            </SimpleForm>
          </Panel>
        </div>
        <div ref={orderFormRef}>
          <Panel title="Create Order">
            {!canUseCustomerProductForms ? <div className="notice">Add at least one customer and one product before creating orders.</div> : null}
            {canUseCustomerProductForms && !orderHasActiveOwner ? <div className="notice">Selected customer is assigned to an inactive sales rep. Reassign the prospect before creating orders.</div> : null}
            {canUseCustomerProductForms && orderHasCreditBlock ? <div className="notice">Selected customer has a credit block from outstanding collections. Resolve the credit block before creating an order.</div> : null}
            <SimpleForm disabled={!canSaveOrder} onSubmit={() => onCreate("/orders", orderForm)}>
              <SelectField
                label="Customer"
                value={orderForm.customerId}
                options={customerOptions}
                disabled={!hasCustomers}
                actionLabel="Add prospect"
                onAction={openCustomer ? () => openCustomer(null) : undefined}
                secondaryActionLabel="Open selected prospect"
                secondaryActionDisabled={!orderForm.customerId}
                onSecondaryAction={openCustomer ? () => openCustomer(orderForm.customerId) : undefined}
                onChange={(customerId) => setOrderForm({ ...orderForm, customerId })}
              />
              <SelectField
                label="Product"
                value={orderForm.productId}
                options={pricedProductOptions}
                disabled={!hasProducts}
                actionLabel="Add product"
                onAction={canManageCatalog ? focusProductEditor : undefined}
                secondaryActionLabel="Edit selected product"
                secondaryActionDisabled={!orderForm.productId}
                onSecondaryAction={canManageCatalog ? () => editSelectedProduct(orderForm.productId) : undefined}
                onChange={(productId) => {
                  const product = resources.products.find((item) => item.id === productId);
                  setOrderForm({ ...orderForm, productId, sellingPrice: product?.sellingPrice ?? orderForm.sellingPrice });
                }}
              />
              <TextField label="Quantity" type="number" value={orderForm.quantity} onChange={(quantity) => setOrderForm({ ...orderForm, quantity })} />
              <TextField label="Selling price" type="number" value={orderForm.sellingPrice} onChange={(sellingPrice) => setOrderForm({ ...orderForm, sellingPrice })} />
              <TextField label="Discount" type="number" value={orderForm.discount} onChange={(discount) => setOrderForm({ ...orderForm, discount })} />
              <SelectField label="Status" value={orderForm.status} options={["Draft", "Confirmed"]} onChange={(status) => setOrderForm({ ...orderForm, status })} />
            </SimpleForm>
          </Panel>
        </div>
        <Panel title="Orders">
          <ResourceTable
            rows={resources.orders}
            columns={[
	              ["customerId", "Customer", (value, row) => <CustomerLink customerId={value} customers={customers} fallbackName={row.customerName} openCustomer={openCustomer} />],
              ["productName", "Product", renderProductReference],
              ["quantity", "Qty"],
              ["total", "Total", currency],
              ["marginPercent", "Margin %"],
              ["status", "Status"],
	              ["userId", "Rep", (value) => <EmployeeLink userId={value} users={users} openEmployee={openEmployee} />],
            ]}
            action={(row) => (
              <div className="row-actions">
                <button
                  className="mini-btn"
                  type="button"
                  disabled={!canPrefillOrder(row)}
                  title={canPrefillOrder(row) ? "Create a new draft from this order" : "Customer or product is no longer active"}
                  onClick={() => startOrderFromOrder(row)}
                >
                  Repeat Order
                </button>
                {openMessage ? (
                  <button
                    className="mini-btn"
                    type="button"
                    disabled={!canMessageCustomer(row.customerId)}
                    title={canMessageCustomer(row.customerId) ? "Send an order update to this customer" : "Customer is no longer active"}
                    onClick={() => startMessageFromOrder(row)}
                  >
                    Message Update
                  </button>
                ) : null}
              </div>
            )}
          />
        </Panel>
      </section>
      <section className="panel spaced">
        <h2>Pricing Approvals</h2>
        <ResourceTable
          rows={resources.pricing}
	          columns={[["productName", "Product", renderProductReference], ["sellingPrice", "Customer Price", currency], ["marginPercent", "Margin %"], ["approvalStatus", "Approval"], ["customerId", "Customer", (value, row) => <CustomerLink customerId={value} customers={customers} fallbackName={row.customerName} openCustomer={openCustomer} />]]}
          action={canManageCatalog ? (row) => (
            <div className="row-actions">
              {row.approvalStatus !== "Approved" ? (
                <AsyncActionButton
                  pendingLabel="Approving..."
                  errorMessage="We could not approve this price. Please try again."
                  onAction={() => onUpdate(`/pricing/${row.id}/approve`)}
                >
                  Approve
                </AsyncActionButton>
              ) : null}
              <button
                className="mini-btn"
                type="button"
                disabled={row.approvalStatus !== "Approved" || !canPrefillOrder(row)}
                title={row.approvalStatus === "Approved" && canPrefillOrder(row) ? "Create an order from this approved price" : "Approve the price before creating an order"}
                onClick={() => startOrderFromPricing(row)}
              >
                Create Order
              </button>
            </div>
          ) : null}
        />
      </section>
      <section className="panel spaced">
        <h2>Product Catalog</h2>
        {canManageCatalog ? <p className="subtle">Delete archives the SKU from active selling screens while keeping historical orders and pricing readable.</p> : null}
        <ResourceTable
          rows={resources.products}
          columns={[["name", "Product", (value, row) => <ProductLink product={row} productName={value} canOpen={canManageCatalog} onOpen={startProductEdit} />], ["sku", "SKU"], ["category", "Category"], ["sellingPrice", "Price", currency], ["availability", "Availability"], ["customizationOptions", "Customization"]]}
          action={canManageCatalog ? (row) => (
            <div className="row-actions">
              <button className="mini-btn" type="button" disabled={!canUseCustomerProductForms} onClick={() => startRecommendationFromProduct(row)}>
                Recommend
              </button>
              <button className="mini-btn" type="button" disabled={!canUseCustomerProductForms} onClick={() => startPricingFromProduct(row)}>
                Price
              </button>
              <button className="mini-btn" type="button" disabled={!canUseCustomerProductForms} onClick={() => startOrderFromProduct(row)}>
                Order
              </button>
              <button className="mini-btn" type="button" onClick={() => startProductEdit(row)}>
                Edit
              </button>
              <AsyncActionButton
                pendingLabel="Deleting..."
                errorMessage="We could not archive this product. Please try again."
                onAction={() => archiveProduct(row)}
              >
                Delete
              </AsyncActionButton>
            </div>
          ) : null}
        />
      </section>
      <section className="panel spaced">
        <h2>Recommendation vs Order</h2>
        <ResourceTable
          rows={resources.fullReport?.recommendationVsOrderReport ?? []}
          columns={[["productName", "Product", renderProductReference], ["customerId", "Customer", (value, row) => <CustomerLink customerId={value} customers={customers} fallbackName={row.customerName} openCustomer={openCustomer} />], ["ordered", "Ordered"], ["gapReason", "Gap Reason"]]}
          action={(row) => row.ordered ? null : (
            <button
              className="mini-btn"
              type="button"
              disabled={!canPrefillOrder(row)}
              title={canPrefillOrder(row) ? "Create an order from this recommendation" : "Customer or product is no longer active"}
              onClick={() => startOrderFromRecommendation(row)}
            >
              Create Order
            </button>
          )}
        />
      </section>
    </>
  );

  function renderProductReference(value, row) {
    return (
      <ProductLink
        product={productByReference(row.productId, value)}
        productName={value}
        canOpen={canManageCatalog}
        onOpen={startProductEdit}
      />
    );
  }

  async function saveProduct() {
    const saved = editingProductId
      ? await onUpdate(`/products/${editingProductId}`, productForm)
      : await onCreate("/products", productForm);
    if (saved === false) throw new Error("Save failed");
    resetProductForm();
  }

	  function startProductEdit(product) {
    setEditingProductId(product.id);
    setProductForm({
      name: product.name || "",
      sku: product.sku || "",
      category: product.category || "",
      costPrice: product.costPrice ?? 0,
      sellingPrice: product.sellingPrice ?? 0,
      availability: product.availability || "Available",
      customizationOptions: product.customizationOptions || "",
    });
    window.setTimeout(() => {
      productEditorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      productEditorRef.current?.querySelector("input")?.focus({ preventScroll: true });
    }, 0);
	  }

	  function focusProductEditor() {
	    setEditingProductId("");
	    setProductForm(emptyProductForm());
	    window.setTimeout(() => {
	      productEditorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
	      productEditorRef.current?.querySelector("input")?.focus({ preventScroll: true });
	    }, 0);
	  }

  function editSelectedProduct(productId) {
    const product = resources.products.find((item) => item.id === productId);
    if (product) startProductEdit(product);
  }

  function canPrefillOrder(row) {
    return Boolean(customers.some((customer) => customer.id === row.customerId) && productByReference(row.productId, row.productName));
  }

  function startOrderFromRecommendation(row) {
    startOrderDraft(row, {
      quantity: 1,
      discount: 0,
      status: "Draft",
    });
  }

  function startOrderFromOrder(row) {
    startOrderDraft(row, {
      quantity: Number(row.quantity || 1),
      sellingPrice: Number(row.sellingPrice || 0),
      discount: Number(row.discount || 0),
      status: "Draft",
    });
  }

  function startOrderFromPricing(row) {
    startOrderDraft(row, {
      quantity: 1,
      sellingPrice: Number(row.sellingPrice || 0),
      discount: Number(row.discount || 0),
      status: "Draft",
    });
  }

  function startOrderFromProduct(product) {
    startOrderDraft({
      customerId: orderForm.customerId || customers[0]?.id || "",
      productId: product.id,
      productName: product.name,
    });
  }

  function startRecommendationFromProduct(product) {
    if (!product?.id) return;
    setRecommendationForm((current) => ({
      ...current,
      productId: product.id,
      need: current.need || `${product.category || "Product"} requirement`,
    }));
    focusLinkedForm(recommendationFormRef);
  }

  function startPricingFromProduct(product) {
    if (!product?.id) return;
    setPricingForm((current) => ({
      ...current,
      productId: product.id,
      sellingPrice: product.sellingPrice ?? current.sellingPrice,
    }));
    focusLinkedForm(pricingFormRef);
  }

  function startOrderDraft(row, overrides = {}) {
    const product = productByReference(row.productId, row.productName);
    if (!product || !customers.some((customer) => customer.id === row.customerId)) return;
    setOrderForm({
      customerId: row.customerId,
      productId: product.id,
      quantity: overrides.quantity ?? 1,
      sellingPrice: overrides.sellingPrice ?? product.sellingPrice ?? 0,
      discount: overrides.discount ?? 0,
      status: overrides.status ?? "Draft",
    });
    focusLinkedFormAfterRender(orderFormRef);
  }

  function focusLinkedForm(ref) {
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    ref.current?.querySelector("select, input")?.focus({ preventScroll: true });
  }

  function focusLinkedFormAfterRender(ref) {
    window.setTimeout(() => {
      focusLinkedForm(ref);
    }, 0);
  }

  function canMessageCustomer(customerId) {
    return Boolean(openMessage && customers.some((customer) => customer.id === customerId));
  }

  function startMessageFromOrder(row) {
    const customer = customers.find((item) => item.id === row.customerId);
    if (!customer || !openMessage) return;
    const productName = row.productName || productByReference(row.productId, row.productName)?.name || "your order";
    openMessage(customer, {
      channel: "WhatsApp",
      template: "Order update",
      message: `Order update: ${productName} is ${row.status || "in progress"} for ${currency(row.total || row.sellingPrice || 0)}.`,
      consentRecorded: customer.consentOptIn !== false,
    });
  }

  function productByReference(productId, productName) {
    return resources.products.find((item) => item.id === productId || item.name === productName) || null;
  }

  async function archiveProduct(product) {
    const confirmed = window.confirm(
      `Archive ${product.name || "this product"} from the active catalog? Historical orders and pricing will remain available.`,
    );
    if (!confirmed) return true;
    const archived = await onUpdate(`/products/${product.id}/archive`, {});
    if (archived !== false && editingProductId === product.id) resetProductForm();
    return archived;
  }

  function resetProductForm() {
    setEditingProductId("");
    setProductForm(emptyProductForm());
  }
}

function emptyProductForm() {
  return { name: "", sku: "", category: "Tiles", costPrice: 0, sellingPrice: 0, availability: "Available", customizationOptions: "" };
}

function ProductLink({ product, productName, canOpen, onOpen }) {
  const label = productName || product?.name || "-";
  if (!canOpen || !product) return label;
  return (
    <button className="table-link-btn" type="button" onClick={() => onOpen(product)}>
      {label}
    </button>
  );
}
